import React, { useState, useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import type { BranchForForm, ILoaderOption, OptionProps } from 'types'

import { FormControl, AutoSuggest } from 'components'
import { Controller, useForm } from 'react-hook-form'
import ReactTooltip from 'react-tooltip'

import { findAll } from 'features/Branch/service'
import { Branch } from 'features/Branch'
import { setSelectedOption } from 'helpers/common'
import { branchEditState } from 'recoils/atoms/branchEditAtom'
import { useRecoilState } from 'recoil'

interface propsProductBranchSearch {
  id: string
  description: string
  branchCode: string
  updateCompany?: (branch: Branch) => void
}

const base = 'f-branch__form'

function ProductBranchSearch(props: propsProductBranchSearch): JSX.Element {
  const [branches, setBranches] = useState<Branch[]>([])

  const [, setBranchForm] = useRecoilState(branchEditState)
  const { control, errors, setValue, trigger, clearErrors } =
    useForm<BranchForForm>({
      defaultValues: {
        branchId: '',
      },
    })

  const [searchCompanyOption, setSearchCBranchOption] =
    useState<ILoaderOption | null>(null)

  const { t } = useTranslation(['branch', 'common', 'company'])

  useEffect(() => {
    const selected = setSelectedOption(props.id, props.description) || null
    setSearchCBranchOption(selected)
  }, [])

  const optionBuilder = (branches?: Branch[]): OptionProps[] | undefined => {
    if (!branches) {
      return
    }

    return branches.map(({ id, name }) => ({
      key: id,
      label: name,
    }))
  }
  const handleSearch = (item: ILoaderOption) => {
    const branchId: string = item.key || ''
    const branch = item as unknown as Branch

    const selected = setSelectedOption(item.key, item.label) || null
    setSearchCBranchOption(selected)
    setValue('branchId', branchId)
    props.updateCompany?.(branch)

    setBranchForm({ ...branch })
    clearErrors('branchId')
  }

  const fetchCompanies = async (
    searchQuery: string,
    loadedOptions: unknown[],
    options: { page: number },
  ) => {
    const payload = {
      ...(searchQuery && {
        'name[like]': `%${searchQuery.trim()}%`,
      }),
      _limit: 10,
      _page: options.page,
      isActive: true,
    }
    const { body: _data, totals } = await findAll(payload)
    const currentTotal = loadedOptions.length + _data.length
    setBranches(_data as unknown as Branch[])

    const optionsWithTooltip = {
      options: _data.map((option) => ({
        value: option?.name,
        label:
          option?.name.length > 40 ? (
            <>
              <ReactTooltip
                role='tooltip'
                className='cb-select-tooltip'
                type='info'
                place='right'
                effect='solid'
                backgroundColor='#3B4050'
                id={option?.id}
              />
              <div
                data-for={option?.id}
                data-tip={option?.name}
                data-event='mouseover focus'
                data-event-off='mouseout blur'
                aria-label='option'
                className='selectOptionWithTooltip'
              >
                {option?.name}
              </div>
            </>
          ) : (
            option.name
          ),
        key: option.id,
        ...option,
      })),
      hasMore: currentTotal < totals,
      additional: {
        page: payload._page + 1,
      },
    }
    return optionsWithTooltip
  }

  return (
    <FormControl
      className={`${base}__item`}
      label={t('branch:name.label')}
      required
    >
      <Controller
        type='text'
        name='branchId'
        control={control}
        render={(field) => {
          const isError =
            Boolean(errors.companyId?.message) &&
            Boolean(optionBuilder(branches))

          return (
            <AutoSuggest
              ref={field.ref}
              placeholder={t('branch:searchBranchName.placeholder')}
              loadOptions={fetchCompanies}
              isError={isError}
              onChange={(item) => {
                field.onChange(item.key)
                handleSearch(item)
              }}
              onBlur={async () => {
                await trigger('branchId')
              }}
              selected={searchCompanyOption || undefined}
              fullWidth
              isSearchable
            />
          )
        }}
      />
    </FormControl>
  )
}

export default ProductBranchSearch
